import { defineComponent as _defineComponent } from 'vue'
import { resolveComponent as _resolveComponent, normalizeClass as _normalizeClass, createVNode as _createVNode, createElementVNode as _createElementVNode, unref as _unref, withCtx as _withCtx, openBlock as _openBlock, createElementBlock as _createElementBlock } from "vue"

const _hoisted_1 = { class: "v-input v-input--density-compact v-input--dirty v-input--readonly v-text-field v-select v-select--single" }
const _hoisted_2 = { class: "input__control" }
const _hoisted_3 = { class: "v-field v-field--active v-field--appended v-field--dirty v-field--has-label v-field--variant-outlined v-theme--opendockTheme" }
const _hoisted_4 = { class: "v-field__field" }
const _hoisted_5 = { class: "v-input v-input--density-compact v-input--dirty v-input--readonly v-text-field v-select v-select--single" }
const _hoisted_6 = { class: "input__control" }
const _hoisted_7 = { class: "v-field v-field--active v-field--appended v-field--dirty v-field--has-label v-field--variant-outlined v-theme--opendockTheme" }
const _hoisted_8 = { class: "v-field__field" }
const _hoisted_9 = { class: "d-flex justify-center justify-self-end" }

import VueDatePicker from '@vuepic/vue-datepicker';
import '@vuepic/vue-datepicker/dist/main.css';
import { reactive, ref, watch } from 'vue';
import { APIs } from '@/enums/APIs';
import { getClient } from '@/services/clientService';
import { GenericArrayOfObjects, INormalizedResponse } from '@/interfaces/api';
import { INovaAppointment, INovaDock, INovaLoadType, INovaWarehouse } from '@/interfaces/nova';
import { exportWorkbook, IExportedData } from '@/services/workbookService';

import {
  cleanWhiteSpace,
  flashErrorsIfExist,
  makeUniqueObjectArray,
  systemNotify
} from '@/services/util';
import { DateTime } from 'luxon';
import WarehouseSelectDialog from '@/components/dialogs/WarehouseSelectDialog.vue';
import ProgressDialog from '@/components/dialogs/ProgressDialog.vue';
import { useProgress } from '@/composables/progressBar';
import config from '@/config/config';
import { NovaApiService } from '@/services/nova/NovaApiService';

const APPT_RANGE_START_DAYS_FROM_NOW = 1;
const APPT_RANGE_END_DAYS_FROM_NOW = 365;

const TESTING_EMAIL_DOMAIN = '@example.com';


export default /*@__PURE__*/_defineComponent({
  __name: 'NovaForm',
  setup(__props) {

const state = reactive({
  orgId: '',
  loading: false,
  shouldShowDialog: false,
  warehouses: [] as GenericArrayOfObjects,
  useTestEmails: config.use_test_emails ? Boolean(config.use_test_emails) : true
});

const startDate = ref();
const endDate = ref();

const progress = reactive(useProgress());

// EXPORT
const exportData = async (warehousesToExport: string[]) => {
  if (!isValid()) {
    return;
  }

  const APPT_RANGE_START_LUXON = startDate.value
    ? DateTime.fromJSDate(startDate.value).startOf('day')
    : DateTime.now().plus({ days: APPT_RANGE_START_DAYS_FROM_NOW }).startOf('day');
  const APPT_RANGE_END_LUXON = endDate.value
    ? DateTime.fromJSDate(endDate.value).endOf('day')
    : DateTime.now().plus({ days: APPT_RANGE_END_DAYS_FROM_NOW });

  state.loading = true;
  const apiService = getClient({
    api: APIs.NOVA,
    token: localStorage.getItem('access_token') as string
  }) as NovaApiService;

  const exportedData: IExportedData = {
    warehouses: [],
    loadTypes: [],
    docks: [],
    appointments: []
  };
  // GET WAREHOUSES
  progress.setDialogVisibility(true);
  progress.createInterval('Fetching Warehouses', 15);
  try {
    const warehouseResponse: INormalizedResponse = await apiService.getWarehouses({
      s: {
        id: {
          $in: warehousesToExport
        }
      }
    });
    if (flashErrorsIfExist(warehouseResponse)) {
      abortExportProcess();
      return false;
    }
    if (state.useTestEmails) {
      warehouseResponse.data = warehouseResponse.data.map(warehouse => {
        if (!cleanWhiteSpace(warehouse.email)) {
          warehouse.email = 'warehouse_placeholder';
        }

        warehouse.ccEmails = getTestEmails(warehouse.ccEmails);
        warehouse.email = getTestEmail(warehouse.email);
        return warehouse;
      });
    }

    exportedData.warehouses = warehouseResponse.data as INovaWarehouse[];
  } catch (e) {
    abortExportProcess();
    return false;
  }

  // GET DOCKS
  progress.createInterval('Fetching Docks', 40);
  try {
    const dockPromises: Promise<INormalizedResponse>[] = warehousesToExport.map(async warehouse => {
      return apiService.getDocks({
        s: {
          warehouseId: {
            $eq: warehouse
          }
        }
      });
    });

    let docks: INovaDock[] = [];
    let errors = false;

    await Promise.all(dockPromises).then(responses => {
      responses.forEach(response => {
        if (flashErrorsIfExist(response)) {
          errors = true;
          abortExportProcess();
          return false;
        }
        docks = [...docks, ...(response.data as INovaDock[])];
      });
    });

    if (errors) {
      return;
    }

    exportedData.docks = makeUniqueObjectArray(docks) as INovaDock[];
  } catch (error: any) {
    systemNotify({ message: error.message, type: 'error' });
    return abortExportProcess();
  }

  // GET LOAD TYPES
  await progress.zoom(40);
  progress.createInterval('Fetching Load Types', 60);
  let loadTypesArr = [] as string[];
  exportedData.docks.forEach(dock => {
    loadTypesArr = [...loadTypesArr, ...(dock.loadTypeIds as string[])];
  });
  const loadTypes = Array.from(new Set(loadTypesArr));
  try {
    const loadTypesResponse: INormalizedResponse = await apiService.getLoadTypes({
      s: {
        id: {
          $in: loadTypes
        }
      }
    });
    if (flashErrorsIfExist(loadTypesResponse)) {
      abortExportProcess();
      return false;
    }
    exportedData.loadTypes = loadTypesResponse.data as INovaLoadType[];
  } catch (error: any) {
    systemNotify({ message: error.message, type: 'error' });
    return abortExportProcess();
  }
  // GET APPOINTMENTS
  await progress.zoom(60);
  progress.createInterval('Fetching Appointments', 98);
  try {
    const appointmentResponse: INormalizedResponse = await apiService.getAppointments({
      s: {
        $and: [
          {
            dockId: {
              $in: exportedData.docks.map(dock => dock.id)
            }
          },
          {
            $and: [
              {
                start: {
                  $between: [APPT_RANGE_START_LUXON, APPT_RANGE_END_LUXON]
                }
              },
              {
                end: {
                  $between: [APPT_RANGE_START_LUXON, APPT_RANGE_END_LUXON]
                }
              }
            ]
          }
        ]
      },
      join: ['user', 'user.company', 'dock']
    });

    let errors = false;

    if (flashErrorsIfExist(appointmentResponse)) {
      errors = true;
    }
    if (state.useTestEmails) {
      appointmentResponse.data = appointmentResponse.data.map(appointment => {
        const newEmails = getTestEmails(appointment.ccEmails);
        appointment.ccEmails = newEmails.join(',');

        return appointment;
      });
    }

    if (errors) {
      abortExportProcess();
      return false;
    }

    exportedData.appointments = appointmentResponse.data as INovaAppointment[];
  } catch (error: any) {
    systemNotify({ message: error.message, type: 'error' });
    abortExportProcess();
    return false;
  }

  exportWorkbook(exportedData, `${APIs.NOVA} export - ${state.orgId}`);
  state.loading = false;
  state.warehouses = [];
  progress.clear();
  await progress.zoom(99, 5);
  progress.update('All done!', 100);
  setTimeout(() => {
    progress.setDialogVisibility(false);
    progress.update('', 0);
  }, 2000);
};

const getTestEmails = (emails: string[]) => {
  const newEmails: string[] = [];
  if (emails?.length > 0 && emails[0]) {
    emails.forEach((email: string) => {
      newEmails.push(getTestEmail(email));
    });
  }
  return newEmails;
};

const getTestEmail = (email: string) => {
  const emailParts = email.split('@');
  return `${emailParts[0]}${TESTING_EMAIL_DOMAIN}`;
};

const abortExportProcess = () => {
  state.loading = false;
  state.shouldShowDialog = false;
  progress.setDialogVisibility(false);
  progress.clear();
  progress.update('', 0);
};

const close = (shouldClearWarehouses = true) => {
  state.shouldShowDialog = false;
  if (shouldClearWarehouses) {
    state.warehouses = [];
  }
};

const loadWarehouses = async () => {
  const apiService = getClient({
    api: APIs.NOVA,
    token: localStorage.getItem('access_token') as string
  }) as NovaApiService;
  state.loading = true;
  const warehouseResponse: INormalizedResponse = await apiService.getWarehouses({
    s: {
      orgId: {
        $eq: state.orgId
      }
    }
  });
  state.loading = false;
  if (flashErrorsIfExist(warehouseResponse)) {
    abortExportProcess();
    return false;
  }
  state.warehouses = warehouseResponse.data;
  state.shouldShowDialog = true;
};

// VALIDATION
const fieldErrors = reactive({
  orgId: ''
});
const errorMessages = {
  orgId: 'A valid Org ID is required'
};
const isValid = () => {
  if (!state.orgId) {
    fieldErrors.orgId = errorMessages.orgId;
  }
  if (fieldErrors.orgId) {
    return false;
  }

  return true;
};

watch(
  state,
  newState => {
    if (newState.orgId) {
      fieldErrors.orgId = '';
    } else {
      fieldErrors.orgId = errorMessages.orgId;
    }
  },
  { deep: true }
);

return (_ctx: any,_cache: any) => {
  const _component_v_text_field = _resolveComponent("v-text-field")!
  const _component_v_switch = _resolveComponent("v-switch")!
  const _component_v_expansion_panel_text = _resolveComponent("v-expansion-panel-text")!
  const _component_v_expansion_panel = _resolveComponent("v-expansion-panel")!
  const _component_v_expansion_panels = _resolveComponent("v-expansion-panels")!

  return (_openBlock(), _createElementBlock("div", null, [
    _createVNode(_component_v_text_field, {
      modelValue: state.orgId,
      "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((state.orgId) = $event)),
      required: "",
      class: _normalizeClass({ text__error: fieldErrors.password }),
      "error-messages": fieldErrors.password,
      "onUpdate:errorMessages": _cache[1] || (_cache[1] = ($event: any) => ((fieldErrors.password) = $event)),
      variant: "outlined",
      density: "compact",
      "prepend-inner-icon": "mdi-domain",
      label: "Org ID"
    }, null, 8, ["modelValue", "class", "error-messages"]),
    _createVNode(_component_v_switch, {
      class: "mt-0",
      modelValue: state.useTestEmails,
      "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((state.useTestEmails) = $event)),
      label: "Use @example for all carrier emails",
      color: "secondary",
      "hide-details": ""
    }, null, 8, ["modelValue"]),
    _createVNode(_component_v_expansion_panels, { class: "my-4" }, {
      default: _withCtx(() => [
        _createVNode(_component_v_expansion_panel, { title: "Advanced Options" }, {
          default: _withCtx(() => [
            _createVNode(_component_v_expansion_panel_text, null, {
              default: _withCtx(() => [
                _cache[7] || (_cache[7] = _createElementVNode("h6", null, "Appointment Export Date Range", -1)),
                _createElementVNode("div", _hoisted_1, [
                  _createElementVNode("div", _hoisted_2, [
                    _createElementVNode("div", _hoisted_3, [
                      _createElementVNode("div", _hoisted_4, [
                        _createVNode(_unref(VueDatePicker), {
                          name: "start-date-picker",
                          "auto-apply": "",
                          teleport: true,
                          "enable-time-picker": false,
                          "month-change-on-scroll": false,
                          "text-input-options": {
                      enterSubmit: true,
                      tabSubmit: true,
                      format: 'MM/dd/yyyy'
                    },
                          class: "date-picker",
                          modelValue: startDate.value,
                          "onUpdate:modelValue": _cache[3] || (_cache[3] = ($event: any) => ((startDate).value = $event))
                        }, null, 8, ["modelValue"])
                      ]),
                      _cache[5] || (_cache[5] = _createElementVNode("div", { class: "v-field__outline" }, [
                        _createElementVNode("div", { class: "v-field__outline__start" }),
                        _createElementVNode("div", { class: "v-field__outline__notch" }, [
                          _createElementVNode("label", {
                            class: "v-label v-field-label v-field-label--floating",
                            "aria-hidden": "true"
                          }, " Appointment Export Start Date ")
                        ]),
                        _createElementVNode("div", { class: "v-field__outline__end" })
                      ], -1))
                    ])
                  ])
                ]),
                _createElementVNode("div", _hoisted_5, [
                  _createElementVNode("div", _hoisted_6, [
                    _createElementVNode("div", _hoisted_7, [
                      _createElementVNode("div", _hoisted_8, [
                        _createVNode(_unref(VueDatePicker), {
                          name: "end-date-picker",
                          "auto-apply": "",
                          teleport: true,
                          "enable-time-picker": false,
                          "month-change-on-scroll": false,
                          "text-input-options": {
                      enterSubmit: true,
                      tabSubmit: true,
                      format: 'MM/dd/yyyy'
                    },
                          class: "date-picker",
                          modelValue: endDate.value,
                          "onUpdate:modelValue": _cache[4] || (_cache[4] = ($event: any) => ((endDate).value = $event))
                        }, null, 8, ["modelValue"])
                      ]),
                      _cache[6] || (_cache[6] = _createElementVNode("div", { class: "v-field__outline" }, [
                        _createElementVNode("div", { class: "v-field__outline__start" }),
                        _createElementVNode("div", { class: "v-field__outline__notch" }, [
                          _createElementVNode("label", {
                            class: "v-label v-field-label v-field-label--floating",
                            "aria-hidden": "true"
                          }, " Appointment Export End Date ")
                        ]),
                        _createElementVNode("div", { class: "v-field__outline__end" })
                      ], -1))
                    ])
                  ])
                ])
              ]),
              _: 1
            })
          ]),
          _: 1
        })
      ]),
      _: 1
    }),
    _createElementVNode("div", _hoisted_9, [
      _createVNode(WarehouseSelectDialog, {
        warehouses: state.warehouses,
        "is-dialog-visible": state.shouldShowDialog,
        onLoadWarehouses: loadWarehouses,
        loading: state.loading,
        onExportData: exportData,
        onClose: close
      }, null, 8, ["warehouses", "is-dialog-visible", "loading"])
    ]),
    _createVNode(ProgressDialog, {
      title: "Exporting Data From Nova",
      "should-show-progress-dialog": progress.shouldShowProgressDialog,
      "current-step-message": progress.currentStepMessage,
      "current-perc": progress.currentPerc
    }, null, 8, ["should-show-progress-dialog", "current-step-message", "current-perc"])
  ]))
}
}

})